/**
 * Leaf drawings — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_drawings`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const leafDrawingsPack: ToilePack = {
    slug: 'leaf-drawings',
    category: 'illustration',
    name: 'Leaf drawings',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'monstera', label: 'Monstera', subject: 'a single monstera leaf with its split holes and the vein pattern drawn in fine detail'},
        {key: 'maple', label: 'Maple leaf', subject: 'an autumn maple leaf curling at its edges, the veins branching out from the stem'},
        {key: 'clover', label: 'Four-leaf clover', subject: 'a four-leaf clover with heart-shaped leaflets on a thin stem and one dewdrop on a leaf'},
    ],
    styles: [
        {key: 'pencil', label: 'Pencil', style: 'graphite pencil drawing, soft layered shading from sharp 2H detail to deep 6B darks, visible paper tooth', palette: 'graphite gray only', framing: 'on plain white paper, the whole sheet used, a thin white margin around the drawing'},
        {key: 'ink', label: 'Pen and ink', style: 'pen and ink drawing, confident contour lines, fine crosshatching and stippling for tone', palette: 'black ink only', framing: 'on clean white paper, centered, a white margin all round'},
        {key: 'charcoal', label: 'Charcoal', style: 'charcoal drawing, smudged tonal masses with crisp accents and lifted highlights', palette: 'black charcoal on white', framing: 'on white drawing paper, the subject filling the sheet, a white edge left clear'},
    ],
    directions: ['a fern', 'a palm leaf', 'a ginkgo'],
    directionHint: 'try: a fern',
    seo: {
        title: 'Leaf Drawings: Monstera, Maple, Four-Leaf Clover',
        metaDescription: 'Nine leaf drawings: a monstera leaf, an autumn maple leaf and a four-leaf clover with a dewdrop, each drawn in pencil, pen and ink, or charcoal.',
        h1: 'Leaf drawings: a monstera leaf, a maple leaf, a four-leaf clover',
        tagline: 'Three leaves, each drawn in graphite pencil, pen and ink, and charcoal.',
        intro: 'Three leaves: a monstera with its split holes and fine veins, an autumn maple leaf curling at the edges, and a four-leaf clover with a dewdrop on one leaf. Each is drawn in pencil, pen and ink, and charcoal, so the veins and the leaf shape can be compared in three media on a square sheet. Type a different leaf or a twist, such as a fern, a palm leaf or a ginkgo, and the same media carry it.',
        useCases: [
            'a vein-level botany study',
            'a lucky-charm card for a friend',
            'a practice sheet for fine lines',
            'a small framed print for a desk',
        ],
        faq: [
            {q: 'What is in this pack?', a: 'Nine drawings of three leaves: a monstera, an autumn maple leaf and a four-leaf clover. Each leaf appears in pencil, pen and ink, and charcoal.'},
            {q: 'Can I ask for a different leaf?', a: 'Yes. Type a leaf or twist, such as a fern, a palm leaf or a ginkgo. A typed direction is a request, not a promised result.'},
            {q: 'How do the three styles differ?', a: 'Pencil shades with soft layers on plain white paper. Pen and ink uses confident contour lines and fine crosshatching. Charcoal uses smudged tone with crisp accents.'},
            {q: 'Is each drawing square?', a: 'Yes. The leaf drawings are square.'},
        ],
    },
}
